import { createContext, useCallback, useContext, type ReactNode } from 'react';

declare global {
  interface Window {
    dataLayer: any[] & { __debug_patched?: boolean };
    _learnq: any[];
    google_tag_manager: {
      [key: string]: {
        dataLayer: {
          push: (data: any) => void;
          reset: () => void;
        };
      };
    }
  }
}
/**
 * Defines the shape of the data and functions that our GTM context will provide.
 */
interface GtmContextType {
  trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
}

/**
 * Creates the React Context with a default dummy function.
 * This prevents errors if a component tries to use the context
 * without being wrapped in the provider.
 */
const GtmContext = createContext<GtmContextType>({
  trackEvent: () => {
    // This is a fallback and should not be called in a normal flow.
    console.warn('GTM trackEvent called outside of a GtmProvider.');
  },
});

/**
 * The GtmProvider component.
 * Wrap your application with this to make the `trackEvent` function available.
 */
export const GtmProvider = ({ children }: { children: ReactNode }) => {
  /**
   * The actual `trackEvent` function that pushes data to the GTM dataLayer.
   * `useCallback` is used to ensure this function reference remains stable
   * across re-renders, preventing unnecessary re-renders in consumer components.
   */
  const trackEvent = useCallback((eventName: string, eventData: Record<string, any> = {}) => {
    if (typeof window.dataLayer === 'undefined') {
      console.warn('GTM dataLayer is not available.');
      return;
    }

    // Push the event to the dataLayer.
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });

  }, []);

  return (
    <GtmContext.Provider value={{ trackEvent }}>{children}</GtmContext.Provider>
  );
};

/**
 * A custom hook to easily access the `trackEvent` function from any component.
 * This is cleaner than importing and using `useContext` directly every time.
 */
export const useGtm = () => useContext(GtmContext);